import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select, message,
  Typography, Tooltip, Popconfirm, Row, Col, Statistic, Badge, Empty,
  Descriptions, Collapse, Alert, Divider, Timeline,
} from 'antd';
import {
  ReloadOutlined, CrownOutlined, DollarOutlined, CheckCircleOutlined,
  ClockCircleOutlined, PhoneOutlined, MailOutlined, GlobalOutlined,
  DeleteOutlined, EyeOutlined, EditOutlined, ExportOutlined,
  SafetyCertificateOutlined, AuditOutlined, RobotOutlined, TeamOutlined,
  TrophyOutlined, WarningOutlined, LockOutlined, FileProtectOutlined,
  EyeInvisibleOutlined, RiseOutlined, BulbOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { partnershipAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const STATUS_MAP = {
  new:         { color: 'blue',    label: 'New' },
  contacted:   { color: 'cyan',    label: 'Contacted' },
  negotiating: { color: 'orange',  label: 'Negotiating' },
  signed:      { color: 'green',   label: 'Signed' },
  declined:    { color: 'default', label: 'Declined' },
};

const TIER_MAP = {
  starter:    { color: '#1890ff', label: 'Marketing Partner ($299)' },
  marketing:  { color: '#1890ff', label: 'Marketing Partner ($299)' },
  popular:    { color: '#D4A03C', label: 'Sales Partner ($799)' },
  sales:      { color: '#D4A03C', label: 'Sales Partner ($799)' },
  elite:      { color: '#722ed1', label: 'Enterprise ($1,499)' },
  enterprise: { color: '#722ed1', label: 'Enterprise ($1,499)' },
  revshare:   { color: '#E8363A', label: 'Revenue Share (100K/mo + 50/50)' },
};

const SectionCard = ({ icon, title, color, children }) => (
  <div style={{ background: '#fafafa', borderRadius: 10, padding: '16px 20px', marginBottom: 12, borderLeft: `4px solid ${color}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      {React.cloneElement(icon, { style: { color, fontSize: 18 } })}
      <Text strong style={{ fontSize: 15 }}>{title}</Text>
    </div>
    {children}
  </div>
);

export default function PartnershipManagePage() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editForm] = Form.useForm();

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnershipAPI.listInquiries();
      setInquiries(res.data || res || []);
    } catch { message.error('Failed to load inquiries'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const handleUpdate = async (values) => {
    try {
      await partnershipAPI.updateInquiry(selected.id, values);
      message.success('Updated');
      setEditOpen(false);
      fetchInquiries();
    } catch { message.error('Update failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await partnershipAPI.deleteInquiry(id);
      message.success('Deleted');
      fetchInquiries();
    } catch { message.error('Delete failed'); }
  };

  const statusCounts = inquiries.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, {});

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => (
        <div>
          <Text strong>{r.full_name}</Text>
          {r.company_name && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{r.company_name}</Text>}
        </div>
      ),
      sorter: (a, b) => (a.full_name || '').localeCompare(b.full_name || ''),
    },
    {
      title: 'Contact',
      key: 'contact',
      responsive: ['md'],
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          {r.email && <div><MailOutlined style={{ marginRight: 4 }} />{r.email}</div>}
          {r.phone && <div><PhoneOutlined style={{ marginRight: 4 }} />{r.phone}</div>}
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      responsive: ['lg'],
      render: (_, r) => (
        <span>{[r.city, r.country].filter(Boolean).join(', ') || '-'}</span>
      ),
    },
    {
      title: 'Tier',
      dataIndex: 'selected_tier',
      width: 220,
      render: (v) => {
        const t = TIER_MAP[v];
        return t ? <Tag color={t.color}>{t.label}</Tag> : <Tag>{v || 'Not selected'}</Tag>;
      },
      filters: Object.entries(TIER_MAP)
        .filter(([k]) => ['starter', 'popular', 'elite', 'revshare'].includes(k))
        .map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (value, record) => record.selected_tier === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (v) => {
        const s = STATUS_MAP[v] || STATUS_MAP.new;
        return <Tag color={s.color}>{s.label}</Tag>;
      },
      filters: Object.entries(STATUS_MAP).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      width: 110,
      responsive: ['lg'],
      render: (v) => v ? new Date(v).toLocaleDateString('zh-TW') : '-',
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      defaultSortOrder: 'descend',
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />}
              onClick={() => { setSelected(r); setDetailOpen(true); }} />
          </Tooltip>
          <Tooltip title="Update Status">
            <Button size="small" icon={<EditOutlined />}
              onClick={() => {
                setSelected(r);
                editForm.setFieldsValue({ status: r.status, admin_notes: r.admin_notes });
                setEditOpen(true);
              }} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title={`Delete inquiry from ${r.full_name}?`}
              onConfirm={() => handleDelete(r.id)} okText="Delete" cancelText="Cancel"
              okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <CrownOutlined style={{ marginRight: 8, color: '#D4A03C' }} />Partnership Inquiries
          </Title>
          <Text type="secondary">Manage partnership applications and revenue-sharing agreements</Text>
        </div>
        <Space>
          <Button icon={<ExportOutlined />}
            onClick={() => window.open('/partnership', '_blank')}>
            View Landing Page
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchInquiries}>Refresh</Button>
        </Space>
      </div>

      {/* ── Partnership Model & Anti-Circumvention Guide ── */}
      <Collapse
        ghost
        style={{ marginBottom: 20, background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0' }}
        items={[{
          key: 'guide',
          label: (
            <Space>
              <SafetyCertificateOutlined style={{ color: '#D4A03C', fontSize: 16 }} />
              <Text strong style={{ fontSize: 15 }}>合作模式與防範機制指南</Text>
              <Tag color="gold">Revenue Share Model</Tag>
            </Space>
          ),
          children: (
            <div>
              {/* Model Overview */}
              <Alert
                type="info"
                showIcon
                icon={<DollarOutlined />}
                style={{ marginBottom: 16, borderRadius: 8 }}
                message="Revenue Share 合作模式概要"
                description={
                  <div style={{ marginTop: 8 }}>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Card size="small" style={{ background: '#f0f7ff', borderColor: '#91caff', borderRadius: 8, textAlign: 'center' }}>
                          <DollarOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                          <div style={{ fontWeight: 700, fontSize: 18, marginTop: 4 }}>10 萬/月</div>
                          <Text type="secondary" style={{ fontSize: 12 }}>合作夥伴月費</Text>
                        </Card>
                      </Col>
                      <Col span={8}>
                        <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591', borderRadius: 8, textAlign: 'center' }}>
                          <TeamOutlined style={{ fontSize: 24, color: '#D4A03C' }} />
                          <div style={{ fontWeight: 700, fontSize: 18, marginTop: 4 }}>50 / 50</div>
                          <Text type="secondary" style={{ fontSize: 12 }}>成交利潤分潤</Text>
                        </Card>
                      </Col>
                      <Col span={8}>
                        <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f', borderRadius: 8, textAlign: 'center' }}>
                          <RobotOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                          <div style={{ fontWeight: 700, fontSize: 18, marginTop: 4 }}>全包</div>
                          <Text type="secondary" style={{ fontSize: 12 }}>AI + 行銷平台 + 廣告</Text>
                        </Card>
                      </Col>
                    </Row>
                    <Paragraph style={{ marginTop: 12, marginBottom: 0, color: '#555' }}>
                      Comboware 提供 AI 員工 + 行銷平台 + 全部廣告投放成本，合作夥伴支付月費 10 萬 THB。
                      成交利潤 50/50 分潤 — 例：成交傭金 100 萬，合作夥伴拿 50 萬，Comboware 拿 50 萬。
                    </Paragraph>
                  </div>
                }
              />

              {/* Risk Analysis */}
              <SectionCard icon={<WarningOutlined />} title="核心風險分析" color="#E8363A">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { risk: '轉單風險', desc: '把 AI 產生的 Lead 交給其他房仲成交，規避 50% 分潤' },
                    { risk: '影子交易', desc: '客戶透過 AI 諮詢，但線下帶看、線下成交不回報' },
                    { risk: '壓低報價', desc: '回報較低傭金金額，私下收取差額' },
                    { risk: '合約到期收割', desc: '合約期間培養的客戶，等到期後才成交' },
                  ].map((item, i) => (
                    <div key={i} style={{ background: '#fff1f0', borderRadius: 8, padding: '8px 12px', border: '1px solid #ffccc7' }}>
                      <Text strong style={{ color: '#E8363A', fontSize: 13 }}>{item.risk}</Text>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{item.desc}</div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <Divider style={{ margin: '16px 0', fontSize: 14, color: '#D4A03C' }}>三道防線</Divider>

              {/* Defense Line 1: Incentive */}
              <SectionCard icon={<TrophyOutlined />} title="第一道防線：激勵設計 — 讓夥伴不想作弊" color="#52c41a">
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f6ffed' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>機制</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>設計</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>效果</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['階梯分潤', '< 500萬 → 50/50｜500萬-1000萬 → 55/45｜> 1000萬 → 60/40', '越誠實報越多，夥伴拿越多'],
                      ['年度獎金', '年度總成交額達標，額外發 2 個月分潤', '長期綁定激勵'],
                      ['AI 升級綁定', '業績好 → 解鎖更強 AI（更多廣告預算、影片、專屬訓練）', '平台價值隨合作加深'],
                      ['品牌背書', '高業績夥伴 → 官方認證 + Partnership 頁面展示', '社會聲譽綁定'],
                    ].map(([a, b, c], i) => (
                      <tr key={i} style={{ background: i % 2 ? '#fafafa' : '#fff' }}>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{a}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>{b}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', color: '#52c41a' }}>{c}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ background: '#e6f7ff', borderRadius: 6, padding: '8px 12px', marginTop: 8, fontSize: 12, color: '#1890ff' }}>
                  <BulbOutlined style={{ marginRight: 6 }} />
                  作弊 vs 誠實算帳：100 萬的單，誠實走平台拿 50 萬（且累積階梯門檻 + 年度獎金）。
                  轉單作弊可能多拿 10-20 萬，但失去階梯加成、年度獎金、AI 升級，加上被發現的 3 倍賠償風險。
                </div>
              </SectionCard>

              {/* Defense Line 2: Tech Controls */}
              <SectionCard icon={<LockOutlined />} title="第二道防線：技術控制 — 讓夥伴很難作弊" color="#1890ff">
                <Timeline
                  style={{ marginTop: 8, marginBottom: 0 }}
                  items={[
                    {
                      color: '#1890ff',
                      children: (
                        <div>
                          <Text strong>客戶資訊管控</Text>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            AI Chatbot 是第一接觸點，客戶資料存在平台。夥伴透過平台內建功能聯繫客戶，所有通訊留紀錄。
                          </div>
                        </div>
                      ),
                    },
                    {
                      color: '#D4A03C',
                      children: (
                        <div>
                          <Text strong>Lead 生命週期追蹤</Text>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            每個 Lead 有狀態：New → Contacted → Viewing → Negotiating → Closed / Lost。
                            長時間停滯或突然標記 Lost → 系統自動警示。
                          </div>
                        </div>
                      ),
                    },
                    {
                      color: '#52c41a',
                      children: (
                        <div>
                          <Text strong>AI 獨立客戶回訪</Text>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            平台 AI 定期對所有 Lead（含標記 Lost 的）發送調查。客戶回覆「已經買了」但夥伴報 Lost → 觸發調查。
                          </div>
                        </div>
                      ),
                    },
                    {
                      color: '#722ed1',
                      children: (
                        <div>
                          <Text strong>公開交易比對</Text>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            泰國房產交易有公開紀錄（土地廳登記）。定期比對 AI 客戶名單 vs. 成交紀錄。
                          </div>
                        </div>
                      ),
                    },
                    {
                      color: '#E8363A',
                      children: (
                        <div>
                          <Text strong>傭金流管控</Text>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            傭金先進 Comboware 帳戶 → 平台自動分帳。或：成交後 7 天內夥伴須匯出 Comboware 應得部分。
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              </SectionCard>

              {/* Defense Line 3: Contract */}
              <SectionCard icon={<FileProtectOutlined />} title="第三道防線：合約懲罰 — 讓作弊代價極高" color="#722ed1">
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9f0ff' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #ddd', width: 160 }}>條款</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>內容</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Lead 歸屬權', '所有 AI 平台產生的 Lead，無論最終由誰成交，皆屬合作範圍，分潤義務存續 18 個月'],
                      ['尾巴條款 (Tail Period)', '合約終止後，合約期間產生的 Lead 在 24 個月內成交，仍須分潤'],
                      ['反規避條款', '夥伴不得將平台 Lead 直接或間接轉介第三方。違反者按該交易應分潤金額的 3 倍賠償'],
                      ['交易透明義務', '所有成交須提供買賣合約影本及傭金收據，Comboware 有權核實'],
                      ['審計權', 'Comboware 可每季查核夥伴交易記錄，夥伴須配合提供相關文件'],
                      ['獨家來源綁定', 'AI 平台產生的客戶，夥伴為唯一對接人，但客戶只能走合作模式'],
                    ].map(([a, b], i) => (
                      <tr key={i} style={{ background: i % 2 ? '#fafafa' : '#fff' }}>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600, color: '#722ed1' }}>{a}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>{b}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SectionCard>

              {/* Operations */}
              <SectionCard icon={<AuditOutlined />} title="日常營運管理" color="#D4A03C">
                <Row gutter={12}>
                  <Col span={8}>
                    <div style={{ background: '#fffbe6', borderRadius: 8, padding: 10, textAlign: 'center', border: '1px solid #ffe58f' }}>
                      <ThunderboltOutlined style={{ fontSize: 20, color: '#D4A03C' }} />
                      <div style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>廣告導流</div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>所有廣告導到 AI Chatbot<br/>不是導到夥伴電話</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ background: '#fffbe6', borderRadius: 8, padding: 10, textAlign: 'center', border: '1px solid #ffe58f' }}>
                      <RiseOutlined style={{ fontSize: 20, color: '#D4A03C' }} />
                      <div style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>月報機制</div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>AI 自動產出月報<br/>Lead 數、轉換率、成交額</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ background: '#fffbe6', borderRadius: 8, padding: 10, textAlign: 'center', border: '1px solid #ffe58f' }}>
                      <EyeInvisibleOutlined style={{ fontSize: 20, color: '#D4A03C' }} />
                      <div style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>品牌認知</div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>客戶認知 HOUSE AI 平台<br/>夥伴是「平台派出的顧問」</div>
                    </div>
                  </Col>
                </Row>
              </SectionCard>

              <Alert
                type="success"
                style={{ borderRadius: 8 }}
                message={
                  <Text strong>三道防線總結</Text>
                }
                description={
                  <div style={{ fontSize: 13 }}>
                    <span style={{ color: '#52c41a', fontWeight: 600 }}>第一道：激勵設計</span> → 讓夥伴不想作弊（階梯分潤、獎金）
                    <br/>
                    <span style={{ color: '#1890ff', fontWeight: 600 }}>第二道：技術控制</span> → 讓夥伴很難作弊（平台追蹤、AI 回訪）
                    <br/>
                    <span style={{ color: '#722ed1', fontWeight: 600 }}>第三道：合約懲罰</span> → 讓作弊代價極高（3 倍賠償、尾巴條款）
                  </div>
                }
              />
            </div>
          ),
        }]}
      />

      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col xs={6}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title="Total" value={inquiries.length} prefix={<GlobalOutlined />} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title="New" value={statusCounts.new || 0} prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title="Negotiating" value={statusCounts.negotiating || 0} prefix={<DollarOutlined />}
              valueStyle={{ color: '#D4A03C' }} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title="Signed" value={statusCounts.signed || 0} prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={inquiries}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: <Empty description="No partnership inquiries yet." /> }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={`Partnership Inquiry: ${selected?.full_name || ''}`}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={<Button onClick={() => setDetailOpen(false)}>Close</Button>}
        width={600}
      >
        {selected && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Full Name">{selected.full_name}</Descriptions.Item>
            <Descriptions.Item label="Company">{selected.company_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Email">{selected.email}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selected.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Country">{selected.country || '-'}</Descriptions.Item>
            <Descriptions.Item label="City">{selected.city || '-'}</Descriptions.Item>
            <Descriptions.Item label="Current Listings">{selected.current_listings || '-'}</Descriptions.Item>
            <Descriptions.Item label="Monthly Sales">{selected.monthly_sales || '-'}</Descriptions.Item>
            <Descriptions.Item label="Selected Tier">
              {TIER_MAP[selected.selected_tier]
                ? <Tag color={TIER_MAP[selected.selected_tier].color}>{TIER_MAP[selected.selected_tier].label}</Tag>
                : (selected.selected_tier || '-')}
            </Descriptions.Item>
            <Descriptions.Item label="Message">{selected.message || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={(STATUS_MAP[selected.status] || STATUS_MAP.new).color}>
                {(STATUS_MAP[selected.status] || STATUS_MAP.new).label}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Admin Notes">{selected.admin_notes || '-'}</Descriptions.Item>
            <Descriptions.Item label="Submitted">{selected.created_at ? new Date(selected.created_at).toLocaleString('zh-TW') : '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Edit Status Modal */}
      <Modal
        title={`Update: ${selected?.full_name || ''}`}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={null}
        width={480}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select>
              {Object.entries(STATUS_MAP).map(([k, v]) => (
                <Select.Option key={k} value={k}>{v.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="admin_notes" label="Admin Notes">
            <Input.TextArea rows={4} placeholder="Internal notes about this inquiry..." />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
